import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "remainingPoints"]
  static values = {
    maxPoints: Number
  }

  connect() {
    this.initializeInputs()
    this.listenToCharismaChanges()
    this.updateMaxPoints()
  }

  initializeInputs() {
    this.inputTargets.forEach(input => {
      input.addEventListener('input', (e) => this.handleSkillChange(e))
    })
    this.updatePoints()
  }

  listenToCharismaChanges() {
    // Find the attribute selection controller and listen for charisma changes
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (attributeController) {
      const charismaInput = attributeController.querySelector('input[name="character[charisma]"]')
      if (charismaInput) {
        charismaInput.addEventListener('input', () => {
          this.updateMaxPoints()
        })
        charismaInput.addEventListener('change', () => {
          this.updateMaxPoints()
        })
      }
      // Initial calculation
      this.updateMaxPoints()
    }
  }

  updateMaxPoints() {
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (attributeController) {
      const charismaInput = attributeController.querySelector('input[name="character[charisma]"]')
      if (charismaInput) {
        const charisma = this.creationEffectiveAttribute(attributeController, charismaInput, 'charisma')
        this.maxPointsValue = charisma * 2
        this.updatePoints()
      }
    } else {
      // Fallback: try to get charisma from the initial value
      const charismaValue = this.element.getAttribute('data-etiquette-selection-max-points-value')
      if (charismaValue) {
        this.maxPointsValue = parseInt(charismaValue, 10) || 0
        this.updatePoints()
      }
    }
  }

  updatePoints() {
    let totalUsedPoints = 0
    this.inputTargets.forEach(input => {
      const value = parseInt(input.value, 10) || 0
      totalUsedPoints += value
    })

    const remainingPoints = this.maxPointsValue - totalUsedPoints
    this.remainingPointsTarget.textContent = `Remaining: ${remainingPoints}`

    // Disable submit button if points exceed limit
    this.syncSubmitState()
  }

  handleSkillChange(event) {
    const input = event.target
    const newValue = parseInt(input.value, 10) || 0
    
    // Calculate current total points used by other skills
    let otherSkillsTotal = 0
    this.inputTargets.forEach(skillInput => {
      if (skillInput !== input) {
        const skillValue = parseInt(skillInput.value, 10) || 0
        otherSkillsTotal += skillValue
      }
    })

    // Calculate maximum points available for this skill
    const availablePoints = this.maxPointsValue - otherSkillsTotal
    
    // Cap the value at the maximum allowed
    const cappedValue = Math.min(newValue, availablePoints, 6) // Also respect the max rank of 6
    
    // Set the capped value
    input.value = cappedValue

    // Store the new value as the last valid value
    input.dataset.lastValue = cappedValue
    
    // Update displays
    this.updatePoints()
  }

  incrementSkill(event) {
    const skillKey = event.target.closest('button').dataset.skill
    const input = document.querySelector(`input[name="character[etiquette_skills][${skillKey}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 0
      const newValue = Math.min(currentValue + 1, 6)
      input.value = newValue
      
      // Trigger the input event to update calculations
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(inputEvent)
    }
  }

  decrementSkill(event) {
    const skillKey = event.target.closest('button').dataset.skill
    const input = document.querySelector(`input[name="character[etiquette_skills][${skillKey}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 0
      const newValue = Math.max(currentValue - 1, 0)
      input.value = newValue
      
      // Trigger the input event to update calculations
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(inputEvent)
    }
  }

  maxPointsValueChanged() {
    this.updatePoints()
  }

  creationEffectiveAttribute(attributeController, input, attributeName) {
    const baseValue = parseInt(input.value, 10) || 0
    const heritageMods = this.getAttributeModifications(attributeController, 'heritage')
    const magicMods = this.getAttributeModifications(attributeController, 'magic')

    return baseValue + (heritageMods[attributeName] || 0) + (magicMods[attributeName] || 0)
  }

  getAttributeModifications(attributeController, source) {
    try {
      const modsValue = attributeController.getAttribute(`data-attribute-selection-${source}-modifications-value`)
      return modsValue ? JSON.parse(modsValue) : {}
    } catch (e) {
      console.warn(`Failed to parse ${source} modifications:`, e)
      return {}
    }
  }

  syncSubmitState() {
    const submitButton = document.querySelector('button[type="submit"]')
    if (!submitButton) return

    const remainingTargets = document.querySelectorAll(
      '[data-attribute-selection-target="remainingPoints"], [data-skill-selection-target="remainingPoints"], [data-knowledge-selection-target="remainingPoints"], [data-etiquette-selection-target="remainingPoints"]'
    )
    const hasNegativeRemaining = Array.from(remainingTargets).some((target) => {
      const match = target.textContent.match(/-?\d+/)
      return match && parseInt(match[0], 10) < 0
    })

    submitButton.disabled = hasNegativeRemaining
    submitButton.classList.toggle('opacity-50', hasNegativeRemaining)
    submitButton.classList.toggle('cursor-not-allowed', hasNegativeRemaining)
  }
}
